import _ from "lodash";

export const OPTIMIZATION_TYPE = {
  DT: "dt",
  CST_V1: "cstv1",
  CST_V2_P: "cstv2p",
  CST_V2_NP: "cstv2np",
};

export const PRICE_TYPE = {
  NO_UPR: "NO_UPR",
  FLOOR: "FLOOR",
  TARGET_CPM: "TARGET_CPM",
  LGO: "LGO",
  ALGO: "ALGO", // only for AutoPilot Config's price_pattern
};

export function getPriceType(price) {
  if (price === null) {
    return { type: PRICE_TYPE.ALGO, value: null };
  }

  if (price === -1) {
    return { type: PRICE_TYPE.NO_UPR, value: null };
  }

  if (price === -2) {
    return { type: PRICE_TYPE.LGO, value: null };
  }

  if (price >= 1000000) {
    return { type: PRICE_TYPE.TARGET_CPM, value: formatCPMFromAPI(price) };
  }

  return { type: PRICE_TYPE.FLOOR, value: price };
}

export function getPrice({ type, value }) {
  if (type === PRICE_TYPE.ALGO) {
    return null;
  }

  if (type === PRICE_TYPE.NO_UPR) {
    return -1;
  }

  if (type === PRICE_TYPE.LGO) {
    return -2;
  }

  if (type === PRICE_TYPE.TARGET_CPM) {
    return formatCPMToAPI(value);
  }

  return _.toNumber(value);
}

export function formatCPMToAPI(value) {
  return _.toNumber((value * 100 + 1000000).toFixed(0));
}

export function formatCPMFromAPI(value) {
  return _.toNumber(((value - 1000000) / 100).toFixed(2));
}

export function getPricesLimit({ optimization }) {
  let min, max;

  switch (optimization) {
    case OPTIMIZATION_TYPE.DT: {
      break;
    }
    case OPTIMIZATION_TYPE.CST_V1:
    case OPTIMIZATION_TYPE.CST_V2_NP:
    case OPTIMIZATION_TYPE.CST_V2_P: {
      min = 1;
      max = 10;
      break;
    }

    default: {
      // nothing
    }
  }

  return { min, max };
}

export function getDetectorPricesLimit({ optimization }) {
  // options: [5,10] (num of prices should be 5 or 10)
  let min, max, options;
  switch (optimization) {
    case OPTIMIZATION_TYPE.DT:
    case OPTIMIZATION_TYPE.CST_V1: {
      min = 1;
      max = 10;
      break;
    }

    case OPTIMIZATION_TYPE.CST_V2_NP:
    case OPTIMIZATION_TYPE.CST_V2_P: {
      // TODO: Check CST Config's traffic_ratio_option_index
      // -> min = max = cstConfig.traffic_ratio_option_index
      // -> 0: 10, 1: 5 (also based on group_traffic_ratio_options)
      // should be 5 or 10...
      options = [5, 10];
      break;
    }

    default: {
      // nothing
    }
  }

  return { min, max, options };
}

export function getCatchallPricesLimit({ optimization }) {
  // options: [5,10] (num of prices should be 5 or 10)
  let min, max, options;
  switch (optimization) {
    case OPTIMIZATION_TYPE.DT:
    case OPTIMIZATION_TYPE.CST_V1:
    case OPTIMIZATION_TYPE.CST_V2_NP: {
      break;
    }

    case OPTIMIZATION_TYPE.CST_V2_P: {
      min = 1;
      max = 10;
      break;
    }

    default: {
      // nothing
    }
  }

  return { min, max, options };
}

export function getXPricesLimit({ optimization }) {
  let min, max;

  switch (optimization) {
    case OPTIMIZATION_TYPE.DT:
    case OPTIMIZATION_TYPE.CST_V1:
    case OPTIMIZATION_TYPE.CST_V2_NP:
    case OPTIMIZATION_TYPE.CST_V2_P: {
      break;
    }

    default: {
      // nothing
    }
  }

  return { min, max };
}

export function isPricesInvalid({ prices, limit }) {
  if (!prices) return false;
  let isInvalid = false;
  let errMsg = null;

  // const { limit } = this.props;
  // limit.options: [5,10] (num of prices could only be 5 or 10)
  const { min, max, options } = limit;

  if (options) {
    if (_.indexOf(options, prices.length) === -1) {
      isInvalid = true;
      errMsg = `Num of prices should be ${options.join(" or ")}`;
      return { isInvalid, errMsg };
      // this.setState({ isInvalid, errMsg });
      // return;
    }
  }

  if (min) {
    if (prices.length < min) {
      isInvalid = true;
      errMsg = `Should have at least ${min} num of prices`;
      return { isInvalid, errMsg };
      // this.setState({ isInvalid, errMsg });
      // return;
    }
  }

  if (max) {
    if (prices.length > max) {
      isInvalid = true;
      errMsg = `Cannot have more than ${max} num of prices`;
      return { isInvalid, errMsg };
      // this.setState({ isInvalid, errMsg });
      // return;
    }
  }

  // no negative
  // round 2 decimals
  for (let p of prices) {
    const { type, value } = getPriceType(p);

    if (type === PRICE_TYPE.FLOOR || type === PRICE_TYPE.TARGET_CPM) {
      if (value < 0) {
        isInvalid = true;
        errMsg = `${value} cannot be negative`;
        return { isInvalid, errMsg };
        // this.setState({ isInvalid, errMsg });
        // break;
      }

      const numOfDecimals = (value.toString().split(".")[1] || []).length;
      if (numOfDecimals > 2) {
        isInvalid = true;

        errMsg = `${value}: only 2 decimal places are allowed`;
        return { isInvalid, errMsg };
        // this.setState({ isInvalid, errMsg });
        // break;
      }
    }
  }

  // this.setState({ isInvalid, errMsg });
  return { isInvalid, errMsg };
}

export function validateUnitSettings(settings) {
  let errors = [];

  if (!settings) return errors;

  // if (!settings || _.isEmpty(settings)) {
  //   errors = ["Cannot be empty"];
  //   return errors;
  // }

  if (!_.isArray(settings)) {
    errors = ["Should be an array"];
    return errors;
  }

  let ids = [];
  let unitIds = [];
  for (const rule of settings) {
    const unitErrors = _validateUnitSettingRule(rule);
    if (unitErrors) {
      const idx = _.indexOf(settings, rule);
      const addIdxErrors = _.map(unitErrors, (e) => `[${idx + 1}] ${e}`);
      errors.push(...addIdxErrors);
      // break;
    }

    // ids are unique
    if (_.indexOf(ids, rule.id) !== -1) {
      errors.push(`ID should be unique. ID ${rule.id} appeared more than once`);
    }
    // units cannot be in multiple settings
    const dupeUnits = _.intersection(unitIds, rule.units);
    if (dupeUnits.length > 0) {
      errors.push(
        `Units cannot appear in multiple rules. ${dupeUnits.join(
          ", "
        )} appears more than once`
      );
    }

    ids.push(rule.id);
    unitIds = unitIds.concat(rule.units);
  }

  return errors.length > 0 ? errors : null;
}

export function _validateUnitSettingRule(rule, tartget = []) {
  let errors = [];
  const {
    id,
    name,
    units,
    prices,
    detector_prices,
    catchall_prices,
    x_prices,
  } = rule;
  // validate:
  // id - int
  // name - string
  // units - array of gamUnitId
  // prices - array of float
  // detector_prices - array of float
  // catchall_prices - array of float
  // x_prices - array of float (remove if empty)

  if (tartget.length === 0 || _.includes(tartget, "id")) {
    if (id === 0 || id < 1) {
      errors.push("id should be larger than 0");
    } else if (!id) {
      errors.push("Missing id");
    } else if (id && !_.isInteger(id)) {
      errors.push(`id should be an integer. ${id} is not an integer`);
    }
  }

  if (tartget.length === 0 || _.includes(tartget, "name")) {
    if (!name) {
      errors.push("Missing name");
    } else if (typeof name !== "string") {
      errors.push("name should be a string");
    }
  }

  if (tartget.length === 0 || _.includes(tartget, "units")) {
    if (!units || units.length === 0) {
      errors.push("Missing units");
    } else if (!_.isArray(units)) {
      errors.push("units should be an array of GAM Unit Ids");
    } else {
      // should be integers
      const invalidUnitIds = _.filter(units, (u) => !_.isInteger(u));
      if (invalidUnitIds.length > 0) {
        errors.push(`units should be integers. ${invalidUnitIds.join(", ")}`);
      }
    }
  }

  if (tartget.length === 0 || _.includes(tartget, "prices")) {
    if (!prices || prices.length === 0) {
      errors.push("Missing prices");
    } else if (!_.isArray(prices)) {
      errors.push("prices should be an array of prices");
    } else {
      // should be integers
      const invalids = _.filter(prices, (u) => !_.isNumber(u));
      if (invalids.length > 0) {
        errors.push(`prices should be numbers. ${invalids.join(", ")}`);
      }
    }
  }

  if (tartget.length === 0 || _.includes(tartget, "detector_prices")) {
    if (!detector_prices || detector_prices.length === 0) {
      errors.push("Missing detector_prices");
    } else if (!_.isArray(detector_prices)) {
      errors.push("detector_prices should be an array of prices");
    } else {
      // should be integers
      const invalids = _.filter(detector_prices, (u) => !_.isNumber(u));
      if (invalids.length > 0) {
        errors.push(
          `detector_prices should be numbers. ${invalids.join(", ")}`
        );
      }
    }
  }

  if (tartget.length === 0 || _.includes(tartget, "catchall_prices")) {
    // catchall prices is optional
    if (catchall_prices) {
      if (catchall_prices.length === 0) {
        errors.push("catchall_prices is empty, please remove it");
      } else if (!_.isArray(catchall_prices)) {
        errors.push("catchall_prices should be an array of prices");
      } else {
        // should be integers
        const invalids = _.filter(catchall_prices, (u) => !_.isNumber(u));
        if (invalids.length > 0) {
          errors.push(
            `catchall_prices should be numbers. ${invalids.join(", ")}`
          );
        }
      }
    }
  }

  if (tartget.length === 0 || _.includes(tartget, "x_prices")) {
    if (x_prices) {
      if (x_prices.length === 0) {
        errors.push("x_prices is empty, please remove it");
      } else if (!_.isArray(x_prices)) {
        errors.push("x_prices should be an array of prices");
      } else {
        // should be integers
        const invalids = _.filter(x_prices, (u) => !_.isNumber(u));
        if (invalids.length > 0) {
          errors.push(`x_prices should be numbers. ${invalids.join(", ")}`);
        }
      }
    }
  }

  return errors.length > 0 ? errors : null;
}
